import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { FilterSelect } from '../../../components/ui';
import { staffApi, GetCreatorsParams, Creator } from '../../../api/staff';
import CreatorsTable from './components/CreatorsTable';
import { ResetFiltersIc } from '../../../components/icons';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';

const CreatorsPage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.creators'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State
  const [creators, setCreators] = useState<Creator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<'-name' | 'name' | 'created_at' | '-created_at'>('created_at');
  const [filters, setFilters] = useState<{
    status?: 'active' | 'wait_confirm' | 'wait_moderation' | 'deactivated' | 'wait_first_deposit' | 'all';
    type?: 'creator' | 'agency' | 'all';
  }>({
    status: undefined,
    type: undefined,
  });

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.creators.filters.status.all') },
    { key: 'active', label: t('admin.creators.filters.status.active') },
    { key: 'wait_confirm', label: t('admin.creators.filters.status.waitConfirm') },
    { key: 'wait_moderation', label: t('admin.creators.filters.status.waitModeration') },
    { key: 'deactivated', label: t('admin.creators.filters.status.deactivated') },
    { key: 'wait_first_deposit', label: t('admin.creators.filters.status.waitFirstDeposit') },
  ], [t]);

  // Type options
  const typeOptions = useMemo(() => [
    { key: 'all', label: t('admin.creators.filters.type.all') },
    { key: 'creator', label: t('admin.creators.filters.type.creator') },
    { key: 'agency', label: t('admin.creators.filters.type.agency') },
  ], [t]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' ||
           (filters.status !== undefined && filters.status !== 'all') ||
           (filters.type !== undefined && filters.type !== 'all');
  }, [searchValue, filters.status, filters.type]);

  // Fetch creators
  const fetchCreators = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetCreatorsParams = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        ...(searchValue && { search: searchValue }),
        ...(filters.status && filters.status !== 'all' && {
          status: filters.status as 'active' | 'wait_confirm' | 'wait_moderation' | 'deactivated' | 'wait_first_deposit'
        }),
        ...(filters.type && filters.type !== 'all' && {
          type: filters.type as 'creator' | 'agency'
        }),
      };

      const response = await staffApi.getCreators(params);
      setCreators(response.objects);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Failed to load creators:', error);
      setCreators([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchValue, filters.status, filters.type, sortBy]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'status') {
      setFilters(prev => ({
        ...prev,
        status: value === 'all' ? undefined : value as 'active' | 'wait_confirm' | 'wait_moderation' | 'deactivated' | 'wait_first_deposit'
      }));
    } else if (key === 'type') {
      setFilters(prev => ({
        ...prev,
        type: value === 'all' ? undefined : value as 'creator' | 'agency'
      }));
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setFilters({
      status: undefined,
      type: undefined,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string) => {
    setSortBy(newSortBy as '-name' | 'name' | 'created_at' | '-created_at');
    setCurrentPage(1);
  };

  return (
    <div className="h-screen flex flex-col ">
      {/* Main Content */}
      <div className="flex-1 ">
        <div className="px-[24px] py-[24px]">
          {/* Title and Filters */}
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              {t('admin.creators.title')}
            </Typography>

            {/* Filters Row */}
            <div className="flex items-center gap-[16px]">
              {/* Search Input */}
              <Input
                type="text"
                placeholder={t('admin.creators.filters.search.placeholder')}
                value={localSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                endContent={
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17.5 17.5L13.875 13.875" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
                classNames={{
                  mainWrapper: "w-[273px]",
                  base: "w-[273px]",
                  input: "text-sm",
                  inputWrapper: "rounded-lg h-[36px] w-[273px]",
                }}
              />

              {/* Status Filter */}
              <FilterSelect
                selectedKey={filters.status || 'all'}
                onSelectionChange={(value) => handleFilterChange('status', value)}
                options={statusOptions}
                placeholder={t('admin.creators.filters.status.placeholder')}
              />

              {/* Type Filter */}
              <FilterSelect
                selectedKey={filters.type || 'all'}
                onSelectionChange={(value) => handleFilterChange('type', value)}
                options={typeOptions}
                placeholder={t('admin.creators.filters.type.placeholder')}
                showPlaceholderWithValue={true}
              />

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <Tooltip content={t('admin.creators.filters.reset') || 'Сбросить фильтры'} placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Table */}
          <div >
            <CreatorsTable
              creators={creators}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              sortBy={sortBy}
              onSort={handleSort}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onRefresh={fetchCreators}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorsPage;
